import { useMutation, useQueryClient } from '@tanstack/react-query'

import { dataOrThrow, type PostPublicBookBody } from '@snootsvet/shared/portal'

import { usePublicBookingApi } from '@/customer/hooks/use-api'
import { publicAvailableSlotsQueryKey } from '@/customer/hooks/use-public-available-slots'
import { publicBookingContextQueryKey } from '@/customer/hooks/use-public-booking-context'

/**
 * Books (or reschedules) an appointment via `POST /api/public/book`.
 * The backend decides which mode we're in based on whether the token
 * is already stored on an appointment.
 *
 * On success, invalidates the booking context so a follow-up read
 * sees the newly-set `currentAppointment`, and the available-slots
 * cache so the taken slot disappears from any still-mounted picker.
 */
export function usePublicBookAppointment(token: string | null) {
  const api = usePublicBookingApi(token)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: PostPublicBookBody) =>
      dataOrThrow(await api.post('/public/book', { body })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: publicBookingContextQueryKey(token) })
      queryClient.invalidateQueries({ queryKey: publicAvailableSlotsQueryKey })
    },
  })
}
